import { getAuth } from 'firebase/auth'
import { kRecentSearchesLimit } from './constants'

const kStorageKeyPrefix = 'matterFilesRecentSearches'

/**
 * Recent queries are stored per user *and* per matter: a shared workstation must not show one
 * user's search history to the next, and a query only makes sense inside the matter it ran against.
 * Returns null when nobody is signed in, which makes every operation a no-op.
 */
function storageKey(matterId: string): string | null {
  const userId = getAuth().currentUser?.uid
  if (!userId) return null

  return `${kStorageKeyPrefix}:${userId}:${matterId}`
}

function parseStored(raw: string | null): string[] {
  if (!raw) return []

  const parsed: unknown = JSON.parse(raw)
  if (!Array.isArray(parsed)) return []

  return parsed.filter((entry): entry is string => typeof entry === 'string' && entry.trim().length > 0).slice(0, kRecentSearchesLimit)
}

/** Most recent first. Returns an empty list rather than throwing on unreadable storage. */
export function readRecentSearches(matterId: string): string[] {
  const key = storageKey(matterId)
  if (!key) return []

  try {
    return parseStored(localStorage.getItem(key))
  } catch (error) {
    console.error('Failed to read recent matter file searches:', error)

    // Its own try: when storage access itself is unavailable rather than the value being corrupt,
    // this throws too — and would escape the effect that called us.
    try {
      localStorage.removeItem(key)
    } catch {
      // Nothing to clean up if storage cannot be touched at all.
    }

    return []
  }
}

/**
 * Moves `query` to the front, dropping any previous run of the same term so repeating a search
 * reorders the list instead of filling it. Returns the stored list.
 */
export function recordRecentSearch(matterId: string, query: string): string[] {
  const key = storageKey(matterId)
  const trimmed = query.trim()
  if (!key || !trimmed) return readRecentSearches(matterId)

  try {
    const existing = parseStored(localStorage.getItem(key))
    const deduped = existing.filter((entry) => entry.toLowerCase() !== trimmed.toLowerCase())
    const next = [trimmed, ...deduped].slice(0, kRecentSearchesLimit)

    localStorage.setItem(key, JSON.stringify(next))
    return next
  } catch (error) {
    console.error('Failed to record a recent matter file search:', error)

    // A failed write must not blank the list on screen — what is already stored is still valid.
    return readRecentSearches(matterId)
  }
}

export function clearRecentSearches(matterId: string): void {
  const key = storageKey(matterId)
  if (!key) return

  try {
    localStorage.removeItem(key)
  } catch (error) {
    console.error('Failed to clear recent matter file searches:', error)
  }
}

/** Logout removes cached query text for every account previously used in this browser. */
export function clearAllRecentSearches(): void {
  try {
    for (let index = localStorage.length - 1; index >= 0; index--) {
      const key = localStorage.key(index)
      if (key?.startsWith(`${kStorageKeyPrefix}:`)) localStorage.removeItem(key)
    }
  } catch (error) {
    console.error('Failed to clear recent matter file searches:', error)
  }
}
